import type { CustomCaseFormCopy } from "../components/custom/CustomCaseFormFields";
import type { CustomCaseFormErrors } from "./customCase";
import type { CustomLandingLang } from "./customLanding";

const FORM_ERROR_MESSAGE_MAP: Record<
  Exclude<CustomLandingLang, "en">,
  Record<string, string>
> = {
  fr: {
    "Please enter a valid email": "Veuillez entrer un email valide",
    "Enter your email before starting checkout.":
      "Entrez votre email avant de lancer le paiement.",
    "Review and accept the privacy and order-access notice before starting checkout.":
      "Veuillez accepter l'avis de confidentialite et d'acces avant de lancer le paiement.",
    "Your first name is required": "Votre prenom est requis",
    "Email is required": "L'email est requis",
    "City is required": "La ville est requise",
    "Please tell us where you heard about us":
      "Dites-nous ou vous avez entendu parler de nous",
    "First name is required": "Le prenom est requis",
    "Age is required": "L'age est requis",
    "Sex is required": "Le sexe est requis",
    "Hair color is required": "La couleur des cheveux est requise",
    "Skin tone is required": "La teinte de peau est requise",
    "Occupation is required": "La profession est requise",
    "Please add at least 1 suspects": "Veuillez ajouter au moins 1 suspect",
    "Suspects should use first names only for custom cases":
      "Utilisez uniquement des prenoms pour les suspects des affaires personnalisees",
  },
  es: {
    "Please enter a valid email": "Introduce un correo valido",
    "Enter your email before starting checkout.":
      "Introduce tu correo antes de iniciar el pago.",
    "Review and accept the privacy and order-access notice before starting checkout.":
      "Revisa y acepta el aviso de privacidad y acceso antes de iniciar el pago.",
    "Your first name is required": "Tu nombre es obligatorio",
    "Email is required": "El correo es obligatorio",
    "City is required": "La ciudad es obligatoria",
    "Please tell us where you heard about us": "Dinos donde nos conociste",
    "First name is required": "El nombre es obligatorio",
    "Age is required": "La edad es obligatoria",
    "Sex is required": "El sexo es obligatorio",
    "Hair color is required": "El color de pelo es obligatorio",
    "Skin tone is required": "El tono de piel es obligatorio",
    "Occupation is required": "La ocupacion es obligatoria",
    "Please add at least 1 suspects": "Anade al menos 1 sospechoso",
    "Suspects should use first names only for custom cases":
      "Usa solo nombres de pila para los sospechosos del caso personalizado",
  },
  de: {
    "Please enter a valid email": "Bitte gib eine gultige E-Mail ein",
    "Enter your email before starting checkout.":
      "Gib deine E-Mail ein, bevor du den Checkout startest.",
    "Review and accept the privacy and order-access notice before starting checkout.":
      "Bitte akzeptiere den Hinweis zu Datenschutz und Bestellzugang, bevor du den Checkout startest.",
    "Your first name is required": "Dein Vorname ist erforderlich",
    "Email is required": "E-Mail ist erforderlich",
    "City is required": "Die Stadt ist erforderlich",
    "Please tell us where you heard about us":
      "Bitte sag uns, wo du von uns erfahren hast",
    "First name is required": "Der Vorname ist erforderlich",
    "Age is required": "Das Alter ist erforderlich",
    "Sex is required": "Das Geschlecht ist erforderlich",
    "Hair color is required": "Die Haarfarbe ist erforderlich",
    "Skin tone is required": "Der Hautton ist erforderlich",
    "Occupation is required": "Der Beruf ist erforderlich",
    "Please add at least 1 suspects":
      "Bitte fuge mindestens 1 verdachtige Person hinzu",
    "Suspects should use first names only for custom cases":
      "Verwende fur individuelle Falle nur Vornamen bei Verdachtigen",
  },
  it: {
    "Please enter a valid email": "Inserisci un'email valida",
    "Enter your email before starting checkout.":
      "Inserisci la tua email prima di avviare il pagamento.",
    "Review and accept the privacy and order-access notice before starting checkout.":
      "Accetta l'avviso sulla privacy e sull'accesso all'ordine prima di avviare il pagamento.",
    "Your first name is required": "Il tuo nome e obbligatorio",
    "Email is required": "L'email e obbligatoria",
    "City is required": "La citta e obbligatoria",
    "Please tell us where you heard about us": "Dicci dove ci hai conosciuti",
    "First name is required": "Il nome e obbligatorio",
    "Age is required": "L'eta e obbligatoria",
    "Sex is required": "Il sesso e obbligatorio",
    "Hair color is required": "Il colore dei capelli e obbligatorio",
    "Skin tone is required": "Il tono della pelle e obbligatorio",
    "Occupation is required": "La professione e obbligatoria",
    "Please add at least 1 suspects": "Aggiungi almeno 1 sospetto",
    "Suspects should use first names only for custom cases":
      "Usa solo i nomi di battesimo per i sospetti del caso personalizzato",
  },
};

export const localizeCustomCaseFormErrors = (
  errors: CustomCaseFormErrors,
  lang: CustomLandingLang,
): CustomCaseFormErrors => {
  if (lang === "en") return errors;

  const translate = (message?: string) =>
    message ? FORM_ERROR_MESSAGE_MAP[lang]?.[message] || message : message;

  return {
    ...errors,
    firstName: translate(errors.firstName),
    email: translate(errors.email),
    city: translate(errors.city),
    location: translate(errors.location),
    source: translate(errors.source),
    suspects: Array.isArray(errors.suspects)
      ? errors.suspects.map((error) => ({
          ...error,
          message: translate(error.message) || error.message,
        }))
      : errors.suspects,
  };
};

export const buildLocalizedCustomCaseFormCopy = (
  lang: CustomLandingLang,
): CustomCaseFormCopy | undefined => {
  if (lang === "fr") {
    return {
      firstNameLabel: "Prenom",
      firstNamePlaceholder: "Votre prenom",
      firstNameHelp: "Nous l'utilisons dans l'accueil audio et la livraison.",
      emailLabel: "Email",
      emailPlaceholder: "vous@email.com",
      sourceLabel: "Ou avez-vous entendu parler de nous ?",
      sourcePlaceholder: "Selectionnez une option",
      sourceOptions: [
        { value: "Google", label: "Google" },
        { value: "YouTube", label: "YouTube" },
        { value: "TikTok", label: "TikTok" },
        { value: "Instagram", label: "Instagram" },
        { value: "Pinterest", label: "Pinterest" },
        { value: "Other", label: "Autre" },
      ],
      cityLabel: "Ville",
      cityPlaceholder: "Entrez le nom de la ville",
      locationLabel: "Lieu (optionnel)",
      locationPlaceholder: "Lieu precis dans la ville",
      suspectsLabel: "Suspects (max 6)",
      suspectsHelp:
        "Entrez uniquement les suspects. N'ajoutez pas la victime ici. Utilisez des prenoms puis ajoutez professions, relations et details d'apparence.",
      suspectsMissing: "Ajoutez au moins un suspect avant l'envoi.",
      devPresetTitle: "Presets de test dev",
      devPresetDescription:
        "Remplacez la liste de suspects en un clic pour tester plus vite en local.",
      suspectCardTitle: "Suspect",
      suspectMissingBadge: "Details manquants",
      suspectFirstNameLabel: "Prenom",
      suspectFirstNamePlaceholder: "Jean",
      ageLabel: "Age",
      agePlaceholder: "30",
      eyesLabel: "Yeux",
      eyesOptions: [
        { value: "Light", label: "Clairs" },
        { value: "Dark", label: "Fonces" },
      ],
      heightLabel: "Taille",
      heightOptions: [
        { value: "Short", label: "Petit" },
        { value: "Average", label: "Moyen" },
        { value: "Tall", label: "Grand" },
      ],
      sexLabel: "Sexe",
      sexOptions: [
        { value: "M", label: "Homme" },
        { value: "F", label: "Femme" },
        { value: "N/A", label: "Prefere ne pas preciser" },
      ],
      hairColorLabel: "Couleur des cheveux",
      hairColorOptions: [
        { value: "Black", label: "Noir" },
        { value: "Brown", label: "Brun" },
        { value: "Blonde", label: "Blond" },
        { value: "Red", label: "Roux" },
        { value: "Gray", label: "Gris" },
        { value: "Bald", label: "Chauve" },
        { value: "Other", label: "Autre" },
      ],
      hairLengthLabel: "Longueur des cheveux",
      hairLengthOptions: [
        { value: "Short", label: "Courts" },
        { value: "Medium", label: "Moyens" },
        { value: "Long", label: "Longs" },
        { value: "Bald", label: "Chauve" },
      ],
      skinToneLabel: "Teinte de peau",
      skinToneOptions: [
        { value: "Light", label: "Claire" },
        { value: "Medium", label: "Moyenne" },
        { value: "Dark", label: "Foncee" },
        { value: "Other", label: "Autre" },
        { value: "Prefer not to specify", label: "Prefere ne pas preciser" },
      ],
      occupationLabel: "Profession",
      occupationPlaceholder: "Developpeur logiciel",
      relationshipLabel: "Relation (optionnel)",
      relationshipPlaceholder: "Ami de la victime",
      notesLabel: "Notes supplementaires (optionnel)",
      notesPlaceholder: "Tout autre detail pour personnaliser l'histoire.",
      addSuspectEmpty:
        "Ajoutez au moins un suspect. Vous pouvez en inclure jusqu'a six.",
      addSuspectMax: "Nombre maximal de suspects atteint (6)",
      addSuspectButton: "Ajouter un suspect",
      selectPlaceholder: "Selectionnez...",
    };
  }

  if (lang === "es") {
    return {
      firstNameLabel: "Nombre",
      firstNamePlaceholder: "Tu nombre",
      firstNameHelp: "Lo usamos en la bienvenida en audio y en la entrega.",
      emailLabel: "Correo",
      emailPlaceholder: "tu@email.com",
      sourceLabel: "Donde nos conociste?",
      sourcePlaceholder: "Selecciona una opcion",
      sourceOptions: [
        { value: "Google", label: "Google" },
        { value: "YouTube", label: "YouTube" },
        { value: "TikTok", label: "TikTok" },
        { value: "Instagram", label: "Instagram" },
        { value: "Pinterest", label: "Pinterest" },
        { value: "Other", label: "Otro" },
      ],
      cityLabel: "Ciudad",
      cityPlaceholder: "Introduce la ciudad",
      locationLabel: "Lugar (opcional)",
      locationPlaceholder: "Lugar concreto dentro de la ciudad",
      suspectsLabel: "Sospechosos (max 6)",
      suspectsHelp:
        "Introduce solo a los sospechosos. No pongas aqui a la victima. Usa nombres de pila y anade profesiones, relaciones y rasgos fisicos.",
      suspectsMissing: "Anade al menos un sospechoso antes de enviar.",
      devPresetTitle: "Presets dev",
      devPresetDescription:
        "Sustituye toda la lista de sospechosos con un clic para probar mas rapido en local.",
      suspectCardTitle: "Sospechoso",
      suspectMissingBadge: "Faltan datos",
      suspectFirstNameLabel: "Nombre",
      suspectFirstNamePlaceholder: "Juan",
      ageLabel: "Edad",
      agePlaceholder: "30",
      eyesLabel: "Ojos",
      eyesOptions: [
        { value: "Light", label: "Claros" },
        { value: "Dark", label: "Oscuros" },
      ],
      heightLabel: "Altura",
      heightOptions: [
        { value: "Short", label: "Baja" },
        { value: "Average", label: "Media" },
        { value: "Tall", label: "Alta" },
      ],
      sexLabel: "Sexo",
      sexOptions: [
        { value: "M", label: "Masculino" },
        { value: "F", label: "Femenino" },
        { value: "N/A", label: "Prefiero no decirlo" },
      ],
      hairColorLabel: "Color de pelo",
      hairColorOptions: [
        { value: "Black", label: "Negro" },
        { value: "Brown", label: "Castano" },
        { value: "Blonde", label: "Rubio" },
        { value: "Red", label: "Pelirrojo" },
        { value: "Gray", label: "Gris" },
        { value: "Bald", label: "Calvo" },
        { value: "Other", label: "Otro" },
      ],
      hairLengthLabel: "Largo del pelo",
      hairLengthOptions: [
        { value: "Short", label: "Corto" },
        { value: "Medium", label: "Medio" },
        { value: "Long", label: "Largo" },
        { value: "Bald", label: "Calvo" },
      ],
      skinToneLabel: "Tono de piel",
      skinToneOptions: [
        { value: "Light", label: "Claro" },
        { value: "Medium", label: "Medio" },
        { value: "Dark", label: "Oscuro" },
        { value: "Other", label: "Otro" },
        { value: "Prefer not to specify", label: "Prefiero no decirlo" },
      ],
      occupationLabel: "Profesion",
      occupationPlaceholder: "Desarrollador de software",
      relationshipLabel: "Relacion (opcional)",
      relationshipPlaceholder: "Amigo de la victima",
      notesLabel: "Notas extra (opcional)",
      notesPlaceholder: "Cualquier detalle que haga la historia mas personal.",
      addSuspectEmpty:
        "Anade al menos un sospechoso. Puedes incluir hasta seis.",
      addSuspectMax: "Has llegado al maximo de sospechosos (6)",
      addSuspectButton: "Anadir sospechoso",
      selectPlaceholder: "Selecciona...",
    };
  }

  if (lang === "de") {
    return {
      firstNameLabel: "Vorname",
      firstNamePlaceholder: "Dein Vorname",
      firstNameHelp:
        "Wir verwenden ihn fur die Audio-Begrussung und die Lieferung.",
      emailLabel: "E-Mail",
      emailPlaceholder: "du@email.com",
      sourceLabel: "Wo hast du von uns erfahren?",
      sourcePlaceholder: "Wahle eine Option",
      sourceOptions: [
        { value: "Google", label: "Google" },
        { value: "YouTube", label: "YouTube" },
        { value: "TikTok", label: "TikTok" },
        { value: "Instagram", label: "Instagram" },
        { value: "Pinterest", label: "Pinterest" },
        { value: "Other", label: "Andere Quelle" },
      ],
      cityLabel: "Stadt",
      cityPlaceholder: "Stadt eingeben",
      locationLabel: "Ort (optional)",
      locationPlaceholder: "Genauer Ort innerhalb der Stadt",
      suspectsLabel: "Verdachtige (max. 6)",
      suspectsHelp:
        "Gib hier nur Verdachtige ein, nicht das Opfer. Verwende Vornamen und erganze Beruf, Beziehung und Aussehen.",
      suspectsMissing:
        "Fuge vor dem Absenden mindestens eine verdachtige Person hinzu.",
      devPresetTitle: "Dev-Testvorlagen",
      devPresetDescription:
        "Ersetze die gesamte Verdachtigenliste mit einem Klick fur schnelleres lokales Testen.",
      suspectCardTitle: "Verdachtige Person",
      suspectMissingBadge: "Details fehlen",
      suspectFirstNameLabel: "Vorname",
      suspectFirstNamePlaceholder: "Lena",
      ageLabel: "Alter",
      agePlaceholder: "30",
      eyesLabel: "Augen",
      eyesOptions: [
        { value: "Light", label: "Hell" },
        { value: "Dark", label: "Dunkel" },
      ],
      heightLabel: "Grosse",
      heightOptions: [
        { value: "Short", label: "Klein" },
        { value: "Average", label: "Mittel" },
        { value: "Tall", label: "Gross" },
      ],
      sexLabel: "Geschlecht",
      sexOptions: [
        { value: "M", label: "Mannlich" },
        { value: "F", label: "Weiblich" },
        { value: "N/A", label: "Mochte ich nicht angeben" },
      ],
      hairColorLabel: "Haarfarbe",
      hairColorOptions: [
        { value: "Black", label: "Schwarz" },
        { value: "Brown", label: "Braun" },
        { value: "Blonde", label: "Blond" },
        { value: "Red", label: "Rot" },
        { value: "Gray", label: "Grau" },
        { value: "Bald", label: "Glatze" },
        { value: "Other", label: "Andere" },
      ],
      hairLengthLabel: "Haarlange",
      hairLengthOptions: [
        { value: "Short", label: "Kurz" },
        { value: "Medium", label: "Mittel" },
        { value: "Long", label: "Lang" },
        { value: "Bald", label: "Glatze" },
      ],
      skinToneLabel: "Hautton",
      skinToneOptions: [
        { value: "Light", label: "Hell" },
        { value: "Medium", label: "Mittel" },
        { value: "Dark", label: "Dunkel" },
        { value: "Other", label: "Andere" },
        { value: "Prefer not to specify", label: "Mochte ich nicht angeben" },
      ],
      occupationLabel: "Beruf",
      occupationPlaceholder: "Softwareentwicklerin",
      relationshipLabel: "Beziehung (optional)",
      relationshipPlaceholder: "Freundin des Opfers",
      notesLabel: "Zusatzliche Notizen (optional)",
      notesPlaceholder: "Alles, was die Geschichte personlicher machen hilft.",
      addSuspectEmpty:
        "Fuge mindestens eine verdachtige Person hinzu. Du kannst bis zu sechs anlegen.",
      addSuspectMax: "Maximale Zahl an Verdachtigen erreicht (6)",
      addSuspectButton: "Verdachtige Person hinzufugen",
      selectPlaceholder: "Auswahlen...",
    };
  }

  if (lang === "it") {
    return {
      firstNameLabel: "Nome",
      firstNamePlaceholder: "Il tuo nome",
      firstNameHelp: "Lo usiamo per l'audio di benvenuto e per la consegna.",
      emailLabel: "Email",
      emailPlaceholder: "tu@email.com",
      sourceLabel: "Dove ci hai scoperti?",
      sourcePlaceholder: "Seleziona un'opzione",
      sourceOptions: [
        { value: "Google", label: "Google" },
        { value: "YouTube", label: "YouTube" },
        { value: "TikTok", label: "TikTok" },
        { value: "Instagram", label: "Instagram" },
        { value: "Pinterest", label: "Pinterest" },
        { value: "Other", label: "Altro" },
      ],
      cityLabel: "Citta",
      cityPlaceholder: "Inserisci la citta",
      locationLabel: "Luogo (opzionale)",
      locationPlaceholder: "Posizione specifica nella citta",
      suspectsLabel: "Sospetti (max 6)",
      suspectsHelp:
        "Inserisci solo i sospetti. Non inserire qui la vittima. Usa i nomi di battesimo e aggiungi professioni, relazioni e dettagli fisici.",
      suspectsMissing: "Aggiungi almeno un sospetto prima di inviare.",
      devPresetTitle: "Preset di test dev",
      devPresetDescription:
        "Sostituisci l'intera lista sospetti con un clic per testare piu velocemente in locale.",
      suspectCardTitle: "Sospetto",
      suspectMissingBadge: "Dettagli mancanti",
      suspectFirstNameLabel: "Nome",
      suspectFirstNamePlaceholder: "Giulia",
      ageLabel: "Eta",
      agePlaceholder: "30",
      eyesLabel: "Occhi",
      eyesOptions: [
        { value: "Light", label: "Chiari" },
        { value: "Dark", label: "Scuri" },
      ],
      heightLabel: "Altezza",
      heightOptions: [
        { value: "Short", label: "Bassa" },
        { value: "Average", label: "Media" },
        { value: "Tall", label: "Alta" },
      ],
      sexLabel: "Sesso",
      sexOptions: [
        { value: "M", label: "Maschile" },
        { value: "F", label: "Femminile" },
        { value: "N/A", label: "Preferisco non specificarlo" },
      ],
      hairColorLabel: "Colore dei capelli",
      hairColorOptions: [
        { value: "Black", label: "Nero" },
        { value: "Brown", label: "Castano" },
        { value: "Blonde", label: "Biondo" },
        { value: "Red", label: "Rosso" },
        { value: "Gray", label: "Grigio" },
        { value: "Bald", label: "Calvo" },
        { value: "Other", label: "Altro" },
      ],
      hairLengthLabel: "Lunghezza dei capelli",
      hairLengthOptions: [
        { value: "Short", label: "Corti" },
        { value: "Medium", label: "Medi" },
        { value: "Long", label: "Lunghi" },
        { value: "Bald", label: "Calvo" },
      ],
      skinToneLabel: "Tono della pelle",
      skinToneOptions: [
        { value: "Light", label: "Chiaro" },
        { value: "Medium", label: "Medio" },
        { value: "Dark", label: "Scuro" },
        { value: "Other", label: "Altro" },
        {
          value: "Prefer not to specify",
          label: "Preferisco non specificarlo",
        },
      ],
      occupationLabel: "Professione",
      occupationPlaceholder: "Sviluppatore software",
      relationshipLabel: "Relazione (opzionale)",
      relationshipPlaceholder: "Amico della vittima",
      notesLabel: "Note extra (opzionale)",
      notesPlaceholder:
        "Qualsiasi dettaglio che renda la storia piu personale.",
      addSuspectEmpty:
        "Aggiungi almeno un sospetto. Puoi inserirne fino a sei.",
      addSuspectMax: "Numero massimo di sospetti raggiunto (6)",
      addSuspectButton: "Aggiungi sospetto",
      selectPlaceholder: "Seleziona...",
    };
  }

  return undefined;
};
